import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { userService } from '../services/userService';
import { safeLog } from '@/utils/safeLog';
import { showNativeNotification } from '@/utils/nativeNotification';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "demo-api-key",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "demo-project.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "demo-project",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "demo-project.appspot.com",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "123456789",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:123456789:web:demo"
};

export const isFirebaseConfigured = () => {
  // M25: Require ALL Firebase vars to be set — partial config causes subtle failures
  const required = [
    import.meta.env.VITE_FIREBASE_PROJECT_ID,
    import.meta.env.VITE_FIREBASE_API_KEY,
    import.meta.env.VITE_FIREBASE_APP_ID,
    import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  ];
  return required.every(v => !!v && v !== 'demo-api-key' && v !== 'demo-project');
};

let app: ReturnType<typeof initializeApp> | null = null;
let messaging: ReturnType<typeof getMessaging> | null = null;

if (isFirebaseConfigured()) {
  try {
    app = initializeApp(firebaseConfig);
    messaging = getMessaging(app);
  } catch (error) {
    safeLog.error('Firebase initialization failed:', error);
  }
} else {
  console.warn('Firebase not configured. Set VITE_FIREBASE_* environment variables.');
}

export class FirebaseMessagingService {
  private vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
  private currentToken: string | null = null;
  private currentAuthUid: string | null = null;
  private refreshListenerActive = false;

  async getFCMToken(): Promise<string | null> {
    try {
      if (!messaging) {
        safeLog.error('Firebase messaging not initialized');
        return null;
      }

      if (!this.vapidKey) {
        safeLog.error('VAPID key not configured');
        return null;
      }

      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      await navigator.serviceWorker.ready;

      const token = await getToken(messaging, {
        vapidKey: this.vapidKey,
        serviceWorkerRegistration: registration
      });

      if (token) {
        return token;
      } else {
        console.warn('FCM: no registration token available.');
        return null;
      }
    } catch (error) {
      safeLog.error('An error occurred while retrieving token:', error);
      return null;
    }
  }

  async registerFCMToken(authUid: string, token: string): Promise<boolean> {
    try {
      const result = await userService.registerFCMToken(authUid, token);
      return result.success;
    } catch (error) {
      safeLog.error('Error registering FCM token:', error);
      return false;
    }
  }

  setupMessageListener() {
    if (!messaging) {
      safeLog.error('Firebase messaging not initialized');
      return;
    }

    onMessage(messaging, (payload) => {

      const data = (payload.data || {}) as Record<string, string>;

      // KAN-13: Self-correction notification (data-only) — show notification and trigger banner refresh
      if (data.type === 'self_correction') {
        window.dispatchEvent(new CustomEvent('self-correction-push', { detail: data }));

        showNativeNotification(data.title || 'Message Flagged — Action Required', {
          body: data.body || 'You have a message to correct',
          icon: '/logo.png',
          tag: `self-correction-${data.correctionId}`,
          requireInteraction: true,
          data,
          onClick: () => window.focus(),
        });
        return;
      }

      // KAN-13: Dismiss self-correction notification
      if (data.type === 'dismiss_self_correction') {
        navigator.serviceWorker.ready.then(reg => {
          reg.getNotifications({ tag: `self-correction-${data.correctionId}` })
            .then(notifications => notifications.forEach(n => n.close()));
        });
        return;
      }

      // Safety alert from detection pipeline — navigate to Alerts page on click
      if (data.type === 'alert') {
        const title = payload.notification?.title || '⚠️ Safety Alert Detected';
        const body = payload.notification?.body || 'A student activity has been flagged.';
        showNativeNotification(title, {
          body,
          icon: '/shlogo.png',
          badge: '/shlogo.png',
          tag: `alert-${data.alertId || Date.now()}`,
          requireInteraction: true,
          data,
          onClick: () => {
            window.focus();
            window.dispatchEvent(new CustomEvent('teacher-navigate', { detail: { page: 'alerts' } }));
          },
        });
        // Fire a custom event so the in-app UI can refresh the alert list
        window.dispatchEvent(new CustomEvent('new-safety-alert', { detail: data }));
        return;
      }

      // Teacher-composed message — show as a foreground notification for the student
      if (data.type === 'teacher_message') {
        const title = payload.notification?.title || 'Message from your teacher'
        const body  = payload.notification?.body  || ''
        if (title) {
          showNativeNotification(title, {
            body,
            icon: '/pwa-maskable-512x512.png',
            badge: '/pwa-maskable-512x512.png',
            tag: `teacher-msg-${data.notificationId || Date.now()}`,
            requireInteraction: true,
            onClick: () => {
              window.focus();
              window.dispatchEvent(new CustomEvent('student-navigate', { detail: { page: 'notifications' } }));
            },
          });
        }
        // Dispatch event so the inbox badge and list refresh immediately
        window.dispatchEvent(new CustomEvent('new-student-notification', { detail: data }))
        return
      }

      if (payload.notification) {
        showNativeNotification(payload.notification.title || 'ShieldED Haven', {
          body: payload.notification.body,
          icon: '/shlogo.png',
          badge: '/shlogo.png',
          tag: 'shieldhaven-notification',
          data,
          onClick: () => window.focus(),
        });
      }
    });
  }

  /**
   * Firebase v9+ removed onTokenRefresh. Instead, re-fetch the token when
   * the page regains visibility and re-register if it changed.
   */
  private startTokenRefreshListener() {
    if (this.refreshListenerActive) return;
    this.refreshListenerActive = true;

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!this.currentAuthUid || !messaging) return;

      try {
        const freshToken = await this.getFCMToken();
        if (freshToken && freshToken !== this.currentToken) {
          const ok = await this.registerFCMToken(this.currentAuthUid, freshToken);
          if (ok) this.currentToken = freshToken;
        }
      } catch (error) {
        safeLog.error('Token refresh check failed:', error);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
  }

  async requestPermission(): Promise<boolean> {
    try {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    } catch (error) {
      safeLog.error('Error requesting notification permission:', error);
      return false;
    }
  }

  isSupported(): boolean {
    return 'Notification' in window && 'serviceWorker' in navigator;
  }

  /**
   * Full FCM lifecycle: request permission → get token → register → listen.
   * @param authUid  The user's Supabase auth UID (used by backend FCM endpoint).
   */
  async initializeForUser(authUid: string): Promise<boolean> {
    try {
      if (!isFirebaseConfigured()) {
        console.log('Firebase not configured, skipping FCM initialization');
        return false;
      }

      if (!this.isSupported()) {
        console.log('Notifications not supported in this browser');
        return false;
      }

      const hasPermission = await this.requestPermission();
      if (!hasPermission) {
        console.log('Notification permission denied');
        return false;
      }

      const token = await this.getFCMToken();
      if (!token) {
        return false;
      }

      const registered = await this.registerFCMToken(authUid, token);
      if (!registered) {
        return false;
      }

      this.currentToken = token;
      this.currentAuthUid = authUid;

      this.setupMessageListener();
      this.startTokenRefreshListener();

      return true;
    } catch (error) {
      safeLog.error('Error initializing FCM:', error);
      return false;
    }
  }

  /**
   * Lightweight variant: get token + register only.
   * Use when the browser already has notification permission (e.g. after
   * the onboarding prompt granted it).
   */
  async getTokenAndRegister(authUid: string): Promise<boolean> {
    try {
      if (!isFirebaseConfigured() || !this.isSupported()) return false;

      const token = await this.getFCMToken();
      if (!token) return false;

      const registered = await this.registerFCMToken(authUid, token);
      if (!registered) return false;

      this.currentToken = token;
      this.currentAuthUid = authUid;

      this.setupMessageListener();
      this.startTokenRefreshListener();

      return true;
    } catch (error) {
      safeLog.error('Error in getTokenAndRegister:', error);
      return false;
    }
  }
}

export const firebaseMessagingService = new FirebaseMessagingService();
export { messaging };
